import moment from 'moment'
import { useEffect, useRef } from 'react'
import { useQuery } from 'components/graphql'
import initShopStatistics from '../pages/Dashboard/gql/initShopStatistics'
import getShopStatisticsData from '../pages/Dashboard/gql/getShopStatisticsData'

const maxRequestTime = 360 * 1000 // 360 seconds request timeout

const useShopStatistics = (props) => {
	const {
		branchList, date,
		cashRegisterIds,
		dateIsToday
	} = props

	const requestStart = useRef(null)
	const { data: initShopData, loading: initLoading } = useQuery(initShopStatistics, {
		variables: {
			params: {
				from: moment.utc(date).startOf('day').toISOString(),
				to: moment.utc(date).endOf('day').toISOString(),
				cashRegisterIds,
				groupBy: 'SHOP'
			}
		},
		fetchPolicy: dateIsToday ? 'no-cache' : 'cache-first',
		onCompleted: () => {
			requestStart.current = new Date().getTime()
		}
	})

	const { data, loading: getLoading, refetch, error } = useQuery(getShopStatisticsData, {
		variables: {
			requestId: initShopData?.initShopStatistics
		},
		skip: initLoading || !initShopData?.initShopStatistics,
		fetchPolicy: dateIsToday ? 'no-cache' : 'cache-first'

	})

	const statisticsStatus = data?.getStatisticsData?.status
	const loadingStatus = statisticsStatus === 'LOADING'
	const readyStatus = statisticsStatus === 'READY'

	// Stable ref to refetch — Apollo 2.x recreates the function on each render, so
	// pulling refetch into the effect deps would tear the polling timer down
	// every render.
	const refetchRef = useRef(refetch)
	refetchRef.current = refetch

	useEffect(() => {
		// READY: clear the retry-window so a follow-up navigation starts fresh.
		if(readyStatus) {
			requestStart.current = null
			return
		}
		// Only poll when the server is still computing and no request is in flight.
		if(!loadingStatus || getLoading) {
			return
		}
		// Cap total polling at maxRequestTime so we don't loop forever.
		const elapsed = requestStart.current ? Date.now() - requestStart.current : 0
		if(elapsed >= maxRequestTime) {
			return
		}
		const id = setTimeout(() => refetchRef.current(), 1500)
		return () => clearTimeout(id)
	}, [loadingStatus, readyStatus, getLoading, statisticsStatus])

	const requestTimeElapsed = new Date().getTime() - requestStart.current

	const stats = data?.getStatisticsData?.data || []

	const statisticsData = stats
		.reduce((acc, { shopId, ...rest }) => {
			// WHEN WE HAVE MORE THAN ONE PACKAGE OF STATISTICAL DATA
			if(acc[shopId]) {
				const {
					total,
					customerCount,
					totalPerRealTimeHours
				} = acc[shopId]
				const {
					total: totalNew,
					customerCount: customerCountNew,
					totalPerRealTimeHours: totalPerRealTimeHoursNew
				} = rest
				acc[shopId] = {
					...acc[shopId],
					total: total + totalNew,
					customerCount: customerCount + customerCountNew,
					totalPerBill: (total + totalNew) / (customerCount + customerCountNew),
					totalPerRealTimeHours: totalPerRealTimeHours + totalPerRealTimeHoursNew
				}
				return acc
			}
			// WHEN WE HAVE ONLY ONE PACKAGE OF STATISTICAL DATA
			acc[shopId] = rest
			return acc
		}, {})

	const branchListWithData = branchList.map(shop => ({
		...shop,
		statistics: statisticsData?.[shop.id] || {}
	}))

	return {
		branchList: branchListWithData,
		loading: initLoading || getLoading || loadingStatus,
		error: requestStart.current !== null && requestTimeElapsed > maxRequestTime ? { message: 'Request time out.' } : error
	}
}

export default useShopStatistics
